import { useMemo } from "react";
import { useApiConfig, installServerErrorInterceptor, SERVER_ERROR_MESSAGE } from "common-fe";
import {
  Configuration,
  ShowsApi,
  ClassesApi,
  RidesApi,
  BreaksApi,
  TestsApi,
  PeopleApi,
  HorsesApi,
  ResultsApi,
  RingStateApi,
  ScoreSheetsApi,
  PresenceApi,
  JudgesApi,
} from "score-common";

// Single place that turns the current auth session into instantiated
// score-common API clients (mirrors scribe-fe / scoreadmin-fe's api/scoreApi.js).
// `useApiConfig` (common-fe) stamps the Cognito bearer token AND the active-org
// X-Org-Id header (via the mounted ActiveOrgProvider) onto every request. Base
// URL comes from VITE_SCORE_API_BASE_URL.
const SCORE_API_BASE_URL = import.meta.env.VITE_SCORE_API_BASE_URL;

// Rewrite every failed API call's error.message to a generic "Server Error"
// (raw detail kept on the error for console logging) so backend codes never
// reach the UI. Installed once on the shared axios singleton at module load.
installServerErrorInterceptor();

export function useScoreApi() {
  const buildConfig = useApiConfig();
  return useMemo(() => {
    const config = new Configuration(buildConfig(SCORE_API_BASE_URL));
    return {
      shows: new ShowsApi(config),
      classes: new ClassesApi(config),
      rides: new RidesApi(config),
      breaks: new BreaksApi(config),
      tests: new TestsApi(config),
      people: new PeopleApi(config),
      horses: new HorsesApi(config),
      results: new ResultsApi(config),
      // #2131 (docs/score/ring-truth.md) — the ring-truth module's data source.
      // Read-only here: announcer-fe never calls setRingState/clearRingState.
      ringState: new RingStateApi(config),
      // #2070 — the batch ScoreSheets-for-rides read (card #1961), same
      // client key scoreadmin-fe already uses (ClassPlacings.jsx, #2063).
      scoreSheets: new ScoreSheetsApi(config),
      // #2366 — scribe presence (GET /presence, #2363). Read-only here:
      // announcer-fe never posts a heartbeat, only scribe-fe does (#2364).
      presence: new PresenceApi(config),
      // #3419 -- the show's judge roster + composite-judge mappings (read-only),
      // so a composite booth lists every real judge, not the FVD run-together name.
      judges: new JudgesApi(config),
    };
  }, [buildConfig]);
}

// JSend unwrap: score-api wraps every success as { status: "success", data }.
// The generated client returns the whole envelope as response.data, so callers
// want response.data.data. Throws on a non-success envelope so a hook's catch
// surfaces it.
export function unwrap(response) {
  const body = response?.data;
  if (!body || body.status !== "success") {
    // A 2xx response carrying a JSend failure envelope — don't leak the backend
    // message/code into the UI; log it and throw the generic message.
    // eslint-disable-next-line no-console
    console.error("[score-api]", body?.data?.message || body?.code, body);
    throw new Error(SERVER_ERROR_MESSAGE);
  }
  return body.data;
}
